import { getStoredToken, hasAuthCookie } from './api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class WSClient {
  constructor() {
    this.ws = null;
    this.listeners = new Set();
    this.reconnectAttempts = 0;
    this.shouldReconnect = true;
  }

  connect() {
    // Phase 19b: prefer cookie auth on the WS handshake. Same-origin browsers
    // attach `tagedin_auth` automatically on the upgrade, so we don't need
    // the token in the URL anymore. Fall back to ?token= for legacy bridge
    // (non-cookie clients) so multi-tab transitions and native callers still
    // work.
    const cookieAuth = hasAuthCookie();
    const token = cookieAuth ? null : getStoredToken();
    if (!cookieAuth && !token) return;
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;
    const proto = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const host = BACKEND_URL.replace(/^https?:\/\//, '');
    const url = cookieAuth
      ? `${proto}://${host}/api/ws`
      : `${proto}://${host}/api/ws?token=${encodeURIComponent(token)}`;
    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      console.warn('ws connect failed', e);
      return;
    }
    this.ws.onopen = () => { this.reconnectAttempts = 0; };
    this.ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        this.listeners.forEach((cb) => cb(data));
      } catch (err) {
        // Malformed message - log and continue, do not break the socket.
        console.warn('[ws] malformed message dropped:', err?.message || err);
      }
    };
    this.ws.onclose = () => {
      if (!this.shouldReconnect) return;
      const delay = Math.min(15000, 500 * Math.pow(2, this.reconnectAttempts++));
      setTimeout(() => this.connect(), delay);
    };
    this.ws.onerror = (ev) => {
      console.warn('[ws] socket error', ev?.message || ev?.type || ev);
      try { this.ws && this.ws.close(); } catch (err) { console.warn('[ws] close after error failed:', err?.message || err); }
    };
  }

  disconnect() {
    this.shouldReconnect = false;
    try { this.ws && this.ws.close(); } catch (err) { console.warn('[ws] disconnect close failed:', err?.message || err); }
  }

  send(payload) {
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  on(cb) { this.listeners.add(cb); return () => this.listeners.delete(cb); }
}

export const ws = new WSClient();
